import { useState, useEffect, useCallback } from "react";
import SortableTableHead, { SortConfig, nextSort, sortData } from "./SortableTableHead";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search,
  RefreshCw,
  Eye,
  Download,
  Plus,
  StickyNote,
  DollarSign,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  RotateCcw,
  Pill,
  Truck,
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface Order {
  id: string;
  patient_id: string | null;
  order_type: string;
  status: string;
  stripe_subscription_id: string | null;
  stripe_payment_intent_id: string | null;
  prescription_id: string | null;
  amount_cents: number;
  currency: string;
  patient_email: string | null;
  patient_name: string | null;
  salesperson_id: string | null;
  commission_rate: number;
  created_at: string;
  updated_at: string;
  batch_number: string | null;
  coa_document_url: string | null;
}

interface OrderNote {
  id: string;
  order_id: string;
  admin_id: string;
  note: string;
  created_at: string;
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive"; icon: any }> = {
  pending: { label: "Pending", variant: "secondary", icon: Clock },
  active: { label: "Active", variant: "default", icon: CheckCircle },
  fulfilled: { label: "Fulfilled", variant: "outline", icon: Package },
  cancelled: { label: "Cancelled", variant: "destructive", icon: XCircle },
  refunded: { label: "Refunded", variant: "destructive", icon: RotateCcw },
};

interface OrderManagementProps {
  patientFilter?: string | null;
  onClearPatientFilter?: () => void;
  onNavigateToPatient?: (patientId: string) => void;
}

const OrderManagement = ({ patientFilter, onClearPatientFilter, onNavigateToPatient }: OrderManagementProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [orderSort, setOrderSort] = useState<SortConfig>({ key: "created_at", direction: "desc" });
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [notes, setNotes] = useState<OrderNote[]>([]);
  const [newNote, setNewNote] = useState("");
  const [notesLoading, setNotesLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newOrder, setNewOrder] = useState({
    patient_email: "",
    patient_name: "",
    order_type: "subscription",
    amount_cents: 0,
    status: "pending",
    salesperson_id: "",
    commission_rate: 10,
  });
  const [salespeople, setSalespeople] = useState<{ user_id: string; name: string }[]>([]);

  // Fetch salespeople for assignment
  useEffect(() => {
    const fetchSalespeople = async () => {
      const { data: roles } = await supabase.from("user_roles").select("user_id").eq("role", "salesperson" as any);
      if (roles && roles.length > 0) {
        const ids = roles.map((r: any) => r.user_id);
        const { data: profiles } = await supabase.from("profiles").select("user_id, first_name, last_name").in("user_id", ids);
        setSalespeople((profiles || []).map((p: any) => ({
          user_id: p.user_id,
          name: `${p.first_name || ""} ${p.last_name || ""}`.trim() || p.user_id.slice(0, 8),
        })));
      }
    };
    fetchSalespeople();
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("orders")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);

    if (statusFilter !== "all") query = query.eq("status", statusFilter);
    if (typeFilter !== "all") query = query.eq("order_type", typeFilter);
    if (patientFilter) query = query.eq("patient_id", patientFilter);

    const { data, error } = await query;
    if (error) {
      toast({ title: "Error loading orders", description: error.message, variant: "destructive" });
    } else {
      setOrders((data as Order[]) || []);
    }
    setLoading(false);
  }, [statusFilter, typeFilter, patientFilter, toast]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const fetchNotes = async (orderId: string) => {
    setNotesLoading(true);
    const { data } = await supabase
      .from("order_notes")
      .select("*")
      .eq("order_id", orderId)
      .order("created_at", { ascending: false });
    setNotes((data as OrderNote[]) || []);
    setNotesLoading(false);
  };

  const openDetail = (order: Order) => {
    setSelectedOrder(order);
    fetchNotes(order.id);
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedOrder || !user) return;
    const { error } = await supabase.from("order_notes").insert({
      order_id: selectedOrder.id,
      admin_id: user.id,
      note: newNote.trim(),
    });
    if (error) {
      toast({ title: "Failed to add note", description: error.message, variant: "destructive" });
    } else {
      setNewNote("");
      fetchNotes(selectedOrder.id);
      toast({ title: "Note added" });
    }
  };

  const updateStatus = async (orderId: string, newStatus: string) => {
    const { error } = await supabase
      .from("orders")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", orderId);
    if (error) {
      toast({ title: "Failed to update status", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Status updated", description: `Order set to ${newStatus}` });
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder((prev) => prev ? { ...prev, status: newStatus } : null);
      }
    }
  };

  const markPrescriptionStatus = async (order: Order, event: "filled" | "shipped") => {
    if (!order.prescription_id || !order.patient_id) {
      toast({ title: "No linked prescription or patient", variant: "destructive" });
      return;
    }

    // 1. Get prescription info
    const { data: rx, error: rxErr } = await supabase
      .from("prescriptions")
      .select("id, peptide_name, status")
      .eq("id", order.prescription_id)
      .single();

    if (rxErr || !rx) {
      toast({ title: "Prescription not found", description: rxErr?.message, variant: "destructive" });
      return;
    }

    const newStatus = event === "shipped" ? "shipped" : "filled";

    // 2. Update prescription status
    const { error: updateErr } = await supabase
      .from("prescriptions")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", order.prescription_id);

    if (updateErr) {
      toast({ title: "Failed to update prescription", description: updateErr.message, variant: "destructive" });
      return;
    }

    // 3. Create patient notification
    let title: string;
    let message: string;
    if (event === "filled") {
      title = "Prescription Filled";
      message = `Great news! Your prescription for ${rx.peptide_name} has been filled by the pharmacy and is being prepared for shipment.`;
    } else {
      title = "Prescription Shipped 📦";
      message = `Your ${rx.peptide_name} prescription has shipped! You'll receive tracking information shortly.`;
    }

    await supabase.from("notifications").insert({
      user_id: order.patient_id,
      title,
      message,
      type: `prescription_${event}`,
      related_id: rx.id,
    });

    // 4. Send transactional email (best-effort)
    if (order.patient_email) {
      try {
        await supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: event === "shipped" ? "prescription-shipped" : "prescription-filled",
            recipientEmail: order.patient_email,
            idempotencyKey: `rx-${event}-${order.prescription_id}`,
            notificationCategory: "prescription_updates",
            templateData: {
              patientName: order.patient_name || undefined,
              peptideName: rx.peptide_name,
            },
          },
        });
      } catch {
        console.log(`Email for ${event} not sent — may not be configured.`);
      }
    }

    // 5. Audit log
    if (user) {
      await supabase.functions.invoke("log-audit", {
        body: {
          action: `admin_mark_${event}`,
          resource_type: "prescription",
          resource_id: order.prescription_id,
          metadata: { order_id: order.id, event },
        },
      });
    }

    toast({ title: `Prescription marked as ${newStatus}`, description: `Patient has been notified.` });
  };

  const createOrder = async () => {
    const { error } = await supabase.from("orders").insert({
      patient_email: newOrder.patient_email || null,
      patient_name: newOrder.patient_name || null,
      order_type: newOrder.order_type,
      amount_cents: newOrder.amount_cents,
      status: newOrder.status,
      salesperson_id: newOrder.salesperson_id || null,
      commission_rate: newOrder.commission_rate,
    });
    if (error) {
      toast({ title: "Failed to create order", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Order created" });
      setCreateOpen(false);
      setNewOrder({ patient_email: "", patient_name: "", order_type: "subscription", amount_cents: 0, status: "pending", salesperson_id: "", commission_rate: 10 });
      fetchOrders();
    }
  };

  const exportCSV = () => {
    const headers = ["ID", "Patient", "Email", "Type", "Status", "Amount", "Created"];
    const rows = filteredOrders.map((o) => [
      o.id,
      o.patient_name || "—",
      o.patient_email || "—",
      o.order_type,
      o.status,
      `${(o.amount_cents / 100).toFixed(2)} ${o.currency.toUpperCase()}`,
      format(new Date(o.created_at), "yyyy-MM-dd HH:mm"),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders-export-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredOrders = sortData(orders.filter((o) => {
    const term = search.toLowerCase();
    return (
      (o.patient_name?.toLowerCase().includes(term) ||
        o.patient_email?.toLowerCase().includes(term) ||
        o.id.includes(term) ||
        o.stripe_subscription_id?.includes(term)) ??
      true
    );
  }), orderSort);

  const formatAmount = (cents: number, currency: string) =>
    `$${(cents / 100).toFixed(2)}`;

  return (
    <div className="space-y-4">
      {patientFilter && (
        <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg text-sm">
          <span className="text-muted-foreground">Filtered by patient</span>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onClearPatientFilter}>
            Clear filter ×
          </Button>
        </div>
      )}
      {/* Toolbar */}
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 w-full">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or order ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36 h-9 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="fulfilled">Fulfilled</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-36 h-9 text-xs">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="subscription">Subscription</SelectItem>
                  <SelectItem value="prescription">Prescription</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchOrders} className="gap-1">
                <RefreshCw size={14} /> Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1">
                <Download size={14} /> Export
              </Button>
              <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1">
                <Plus size={14} /> New Order
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Order Table */}
      <Card className="border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <p className="text-center text-muted-foreground py-16">No orders found</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead label="Order" sortKey="id" currentSort={orderSort} onSort={(k) => setOrderSort(nextSort(orderSort, k))} />
                    <SortableTableHead label="Patient" sortKey="patient_name" currentSort={orderSort} onSort={(k) => setOrderSort(nextSort(orderSort, k))} />
                    <SortableTableHead label="Type" sortKey="order_type" currentSort={orderSort} onSort={(k) => setOrderSort(nextSort(orderSort, k))} />
                    <SortableTableHead label="Status" sortKey="status" currentSort={orderSort} onSort={(k) => setOrderSort(nextSort(orderSort, k))} />
                    <SortableTableHead label="Amount" sortKey="amount_cents" currentSort={orderSort} onSort={(k) => setOrderSort(nextSort(orderSort, k))} />
                    <SortableTableHead label="Date" sortKey="created_at" currentSort={orderSort} onSort={(k) => setOrderSort(nextSort(orderSort, k))} />
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => {
                    const sc = statusConfig[order.status] || statusConfig.pending;
                    const StatusIcon = sc.icon;
                    return (
                      <TableRow key={order.id} className="cursor-pointer hover:bg-secondary/30" onClick={() => openDetail(order)}>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {order.id.slice(0, 8)}…
                        </TableCell>
                        <TableCell>
                          <div>
                            {order.patient_id && onNavigateToPatient ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); onNavigateToPatient(order.patient_id!); }}
                                className="text-sm font-medium text-primary hover:underline text-left"
                              >
                                {order.patient_name || "—"}
                              </button>
                            ) : (
                              <p className="text-sm font-medium">{order.patient_name || "—"}</p>
                            )}
                            <p className="text-xs text-muted-foreground">{order.patient_email || "—"}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs capitalize">
                            {order.order_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={sc.variant} className="text-xs gap-1">
                            <StatusIcon size={12} /> {sc.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatAmount(order.amount_cents, order.currency)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {format(new Date(order.created_at), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openDetail(order); }}>
                            <Eye size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={(open) => !open && setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl flex items-center gap-2">
                  <Package size={18} /> Order Details
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-2">
                {/* Order Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Order ID</span>
                    <p className="font-mono text-xs mt-0.5">{selectedOrder.id}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type</span>
                    <p className="capitalize mt-0.5">{selectedOrder.order_type}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Patient</span>
                    <p className="mt-0.5">{selectedOrder.patient_name || "—"}</p>
                    <p className="text-xs text-muted-foreground">{selectedOrder.patient_email || "—"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Amount</span>
                    <p className="font-semibold mt-0.5">{formatAmount(selectedOrder.amount_cents, selectedOrder.currency)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created</span>
                    <p className="mt-0.5">{format(new Date(selectedOrder.created_at), "MMM d, yyyy h:mm a")}</p>
                  </div>
                  {selectedOrder.stripe_subscription_id && (
                    <div>
                      <span className="text-muted-foreground">Stripe Sub ID</span>
                      <p className="font-mono text-xs mt-0.5">{selectedOrder.stripe_subscription_id}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Salesperson</span>
                    <Select
                      value={selectedOrder.salesperson_id || "none"}
                      onValueChange={async (val) => {
                        const newVal = val === "none" ? null : val;
                        const { error } = await supabase
                          .from("orders")
                          .update({ salesperson_id: newVal, updated_at: new Date().toISOString() })
                          .eq("id", selectedOrder.id);
                        if (error) {
                          toast({ title: "Failed to reassign", description: error.message, variant: "destructive" });
                        } else {
                          setSelectedOrder((prev) => prev ? { ...prev, salesperson_id: newVal } : null);
                          toast({ title: newVal ? "Salesperson reassigned" : "Salesperson removed" });
                          fetchOrders();
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1 w-full text-sm">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {salespeople.map((sp) => (
                          <SelectItem key={sp.user_id} value={sp.user_id}>{sp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Commission ({selectedOrder.commission_rate}%)</span>
                    <p className="font-semibold text-primary mt-0.5">
                      {formatAmount((selectedOrder.amount_cents * selectedOrder.commission_rate) / 100, selectedOrder.currency)}
                    </p>
                  </div>
                </div>

                {/* Status Update */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Update Status</label>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(statusConfig).map(([key, cfg]) => {
                      const Icon = cfg.icon;
                      return (
                        <Button
                          key={key}
                          variant={selectedOrder.status === key ? "default" : "outline"}
                          size="sm"
                          className="gap-1 text-xs"
                          onClick={() => updateStatus(selectedOrder.id, key)}
                          disabled={selectedOrder.status === key}
                        >
                          <Icon size={12} /> {cfg.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Pharmacy Fulfillment */}
                {selectedOrder.prescription_id && selectedOrder.patient_id && (
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">Pharmacy Fulfillment</label>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 text-xs"
                        onClick={() => markPrescriptionStatus(selectedOrder, "filled")}
                      >
                        <Pill size={12} /> Mark as Filled
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 text-xs"
                        onClick={() => markPrescriptionStatus(selectedOrder, "shipped")}
                      >
                        <Truck size={12} /> Mark as Shipped
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Patient will be notified when you update the status.</p>
                  </div>
                )}

                {/* Batch & COA Tracking */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Batch & COA Tracking</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Batch / Lot Number</label>
                      <Input
                        placeholder="e.g. LOT-2026-04-001"
                        defaultValue={selectedOrder.batch_number || ""}
                        onBlur={async (e) => {
                          const val = e.target.value.trim() || null;
                          if (val !== selectedOrder.batch_number) {
                            await supabase.from("orders").update({ batch_number: val }).eq("id", selectedOrder.id);
                            setSelectedOrder((prev) => prev ? { ...prev, batch_number: val } : null);
                            toast({ title: "Batch number saved" });
                          }
                        }}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">COA Document URL</label>
                      <Input
                        placeholder="https://..."
                        defaultValue={selectedOrder.coa_document_url || ""}
                        onBlur={async (e) => {
                          const val = e.target.value.trim() || null;
                          if (val !== selectedOrder.coa_document_url) {
                            await supabase.from("orders").update({ coa_document_url: val }).eq("id", selectedOrder.id);
                            setSelectedOrder((prev) => prev ? { ...prev, coa_document_url: val } : null);
                            toast({ title: "COA URL saved" });
                          }
                        }}
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">Batch number and COA link are displayed on the patient's invoice.</p>
                </div>

                {/* Notes */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <StickyNote size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium">Admin Notes</span>
                  </div>

                  <div className="flex gap-2 mb-3">
                    <Textarea
                      placeholder="Add a note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="min-h-[60px] text-sm"
                    />
                    <Button onClick={addNote} disabled={!newNote.trim()} className="shrink-0 self-end">
                      Add
                    </Button>
                  </div>

                  {notesLoading ? (
                    <div className="flex justify-center py-4">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : notes.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">No notes yet</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {notes.map((n) => (
                        <div key={n.id} className="p-3 rounded-lg bg-secondary/50 border border-border">
                          <p className="text-sm">{n.note}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {format(new Date(n.created_at), "MMM d, h:mm a")} · Admin {n.admin_id.slice(0, 8)}…
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Order Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Create New Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Patient Name</label>
              <Input value={newOrder.patient_name} onChange={(e) => setNewOrder((p) => ({ ...p, patient_name: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Patient Email</label>
              <Input type="email" value={newOrder.patient_email} onChange={(e) => setNewOrder((p) => ({ ...p, patient_email: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Order Type</label>
              <Select value={newOrder.order_type} onValueChange={(v) => setNewOrder((p) => ({ ...p, order_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="subscription">Subscription</SelectItem>
                  <SelectItem value="prescription">Prescription</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Amount ($)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={(newOrder.amount_cents / 100).toFixed(2)}
                onChange={(e) => setNewOrder((p) => ({ ...p, amount_cents: Math.round(parseFloat(e.target.value || "0") * 100) }))}
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Status</label>
              <Select value={newOrder.status} onValueChange={(v) => setNewOrder((p) => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="fulfilled">Fulfilled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Assign Salesperson</label>
              <Select value={newOrder.salesperson_id} onValueChange={(v) => setNewOrder((p) => ({ ...p, salesperson_id: v }))}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {salespeople.map((sp) => (
                    <SelectItem key={sp.user_id} value={sp.user_id}>{sp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Commission Rate (%)</label>
              <Input type="number" step="0.5" min="0" max="100" value={newOrder.commission_rate}
                onChange={(e) => setNewOrder((p) => ({ ...p, commission_rate: parseFloat(e.target.value || "0") }))} />
            </div>
            <Button onClick={createOrder} className="w-full">Create Order</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderManagement;
